import { useEffect, useRef, useState } from "react";
import { callFunction } from "../lib/supabase.js";
import { IntakeQuestionnaire } from "../dashboard.jsx";
import { Loading, Message } from "./common.jsx";

const SAVE_EVERY_MS = 2500;

// /intake           → public "Get started" form (a resume link is emailed after step 1)
// /intake/<token>   → personal invite or resume link
export function PublicIntake({ token: initialToken }) {
  const [state, setState] = useState(initialToken ? undefined : { draft: null, studioName: null, status: "new" });
  const [err, setErr] = useState("");
  const tokenRef = useRef(initialToken);
  const studioSlug = new URLSearchParams(location.search).get("s") || undefined;

  // Autosave queue (gap #13): at most one save every 2.5 s, always sending the latest answers.
  // If the server says "slow down", the answers stay here and go out on the next try.
  const pending = useRef(null), timer = useRef(null), nextAt = useRef(0), sending = useRef(false), stopped = useRef(false);
  const pump = () => {
    clearTimeout(timer.current);
    if (!pending.current || !tokenRef.current || stopped.current) return;
    const wait = nextAt.current - Date.now();
    if (sending.current || wait > 0) { timer.current = setTimeout(pump, Math.max(wait, 250)); return; }
    const draft = pending.current;
    pending.current = null; sending.current = true; nextAt.current = Date.now() + SAVE_EVERY_MS;
    callFunction("intake", { action: "save", token: tokenRef.current, form: draft.form, step: draft.step })
      .catch(e => {
        if (!pending.current) pending.current = draft;           // keep the unsaved answers
        if (e.code === "SAVE_LIMIT") stopped.current = true;      // "Send it in" still carries everything
        nextAt.current = Date.now() + (e.code === "SLOW_DOWN" ? 5000 : 4000);
      })
      .finally(() => { sending.current = false; if (pending.current) timer.current = setTimeout(pump, 250); });
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!initialToken) {
      callFunction("intake", { action: "studio-info", studio: studioSlug }).then(r => setState(s => ({ ...s, studioName: r.studioName, packages: r.packages }))).catch(() => {});
      return;
    }
    callFunction("intake", { action: "get", token: initialToken })
      .then(r => setState({ draft: r.draft, studioName: r.studioName, status: r.status, packages: r.packages }))
      .catch(e => { setErr(e.message); setState(null); });
  }, [initialToken]);

  if (state === undefined) return <Loading />;
  if (state === null) return <Message title="This link isn't working" body={err || "The link may have expired. Ask your planner for a new one."} />;
  if (state.status === "submitted") return <Message title="Already in my hands." body={"I already have your answers, thank you. I'll be in touch soon about next steps."} />;

  const saveDraft = (form, step) => {
    if (!tokenRef.current) return;
    pending.current = { form, step };
    pump();
  };

  // The first time a public visitor finishes step 1 we create their draft and email them a resume link.
  const onStepDone = async (step, form) => {
    if (tokenRef.current || step !== 0) return;
    const r = await callFunction("intake", { action: "start", studio: studioSlug, form, step: 1 });
    tokenRef.current = r.token;
    history.replaceState(null, "", "/intake/" + r.token + location.search);
  };

  const onSubmit = async form => {
    if (!tokenRef.current) {
      const r = await callFunction("intake", { action: "start", studio: studioSlug, form, step: 6 });
      tokenRef.current = r.token;
    }
    const r = await callFunction("intake", { action: "submit", token: tokenRef.current, form });
    return { names: r.names };
  };

  return (
    <IntakeQuestionnaire
      variant="public"
      studioName={state.studioName}
      packages={state.packages}
      initialDraft={state.draft}
      onSaveDraft={saveDraft}
      onStepDone={onStepDone}
      onSubmit={onSubmit}
      notice={!initialToken ? null : <div className="field-hint">Your answers save as you go. You can close this page and come back with the same link.</div>}
    />
  );
}
